import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import PageHeader from "components/PageLayout";
import Skeleton from "components/Skeleton";
import ConfirmDialog from "components/ConfirmDialog";
import { useAuth, requireAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useToast } from "components/Toast";
import { useRequestTypeAdmin, useRequestTypesAdmin } from "hooks/useRequestTypesAdmin";
import { TICKET_PRIORITY_LABELS } from "constants/tickets";
import {
  REQUEST_TYPE_PRIORITIES,
  requestTypeDraft,
  requestTypeIssues,
  requestTypeBody,
  describeRequestTypeError,
  suggestRequestTypeKey,
} from "utilities/requestTypes";
import { ArrowLeft, Tags, AlertTriangle, Send, Timer } from "lucide-react";

/**
 * RequestTypeEditor (SOUND-544) — create / edit one request type. The key is
 * entered on CREATE only and is read-only afterwards (the API 400s a changed
 * key). Activate / Deactivate are their own endpoints; a refused deactivate
 * (409) renders its dependants in full on the page — never swallowed.
 */
function RequestTypeEditor() {
  const { key: routeKey } = useParams();
  const isNew = !routeKey || routeKey === "new";
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = useEffectiveCompanyId();
  const authUser = { id: user.id, accessToken: user.accessToken };
  const { toast } = useToast();

  const { requestType, loading, error } = useRequestTypeAdmin(routeKey);
  const { requestTypes: allTypes } = useRequestTypesAdmin({ includeInactive: true });

  const [draft, setDraft] = useState(() => requestTypeDraft(null));
  const [active, setActive] = useState(true);
  const [keyEdited, setKeyEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showIssues, setShowIssues] = useState(false);
  const [deactivateConfirm, setDeactivateConfirm] = useState(false);
  // The last refused write, shown in full until the next attempt.
  const [refusal, setRefusal] = useState(null);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!isNew && requestType && !hydratedRef.current) {
      setDraft(requestTypeDraft(requestType));
      setActive(requestType.active !== false);
      hydratedRef.current = true;
    }
  }, [isNew, requestType]);

  const existingKeys = useMemo(() => allTypes.map((t) => t.key), [allTypes]);
  const issues = useMemo(
    () => requestTypeIssues(draft, { isNew, existingKeys }),
    [draft, isNew, existingKeys]
  );

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const onLabelChange = (label) => {
    // Suggest a key from the label on create until the author types one.
    if (isNew && !keyEdited) set({ label, key: suggestRequestTypeKey(label) });
    else set({ label });
  };

  const save = async () => {
    if (issues.length > 0) {
      setShowIssues(true);
      toast({ status: "warning", title: "Fix the highlighted fields first" });
      return;
    }
    setSaving(true);
    setRefusal(null);
    try {
      const body = requestTypeBody(draft, { isNew });
      const res = isNew
        ? await makeApiRequest("post", apiRoutes.createTicketRequestType, authUser, companyId, body)
        : await makeApiRequest("put", apiRoutes.updateTicketRequestType(routeKey), authUser, companyId, body);
      const saved = res.data?.data;
      setShowIssues(false);
      toast({ status: "success", title: isNew ? "Request type created" : "Request type saved" });
      if (isNew && saved?.key) {
        hydratedRef.current = false;
        navigate(`/settings/request-types/${encodeURIComponent(saved.key)}`, { replace: true });
      } else if (saved) {
        setDraft(requestTypeDraft(saved));
        setActive(saved.active !== false);
      }
    } catch (err) {
      const d = describeRequestTypeError(err, isNew ? "Create failed" : "Save failed");
      setRefusal(d);
      toast({ status: "destructive", title: d.title, description: d.message });
    } finally {
      setSaving(false);
    }
  };

  const setActivation = async (nextActive) => {
    setSaving(true);
    setRefusal(null);
    try {
      const route = nextActive
        ? apiRoutes.activateTicketRequestType(routeKey)
        : apiRoutes.deactivateTicketRequestType(routeKey);
      const res = await makeApiRequest("post", route, authUser, companyId);
      const updated = res.data?.data;
      setActive(updated ? updated.active !== false : nextActive);
      toast({ status: "success", title: nextActive ? "Request type activated" : "Request type deactivated" });
    } catch (err) {
      const d = describeRequestTypeError(err, nextActive ? "Activate failed" : "Deactivate failed");
      setRefusal(d);
      toast({ status: "destructive", title: d.title, description: d.message });
    } finally {
      setSaving(false);
    }
  };

  const leave = () => navigate("/settings/request-types");

  if (!isNew && loading) {
    return (
      <div>
        <PageHeader title={<Skeleton.Rect className="h-8 w-64" />} />
        <div className="container space-y-6 py-6">
          <Skeleton.Rect className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!isNew && !requestType && error) {
    return (
      <div>
        <PageHeader title="Request type" />
        <div className="container py-6">
          <div className="rounded-lg border border-destructive/30 bg-destructive/[0.06] p-4 text-sm">
            {error.response?.status === 404
              ? `No request type with key "${routeKey}".`
              : error.response?.data?.error || error.message || "Failed to load"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        sticky
        title={
          <span className="inline-flex items-center gap-2">
            <Tags className="h-5 w-5 text-brand" />
            {isNew ? "New request type" : draft.label || routeKey}
          </span>
        }
        badge={
          !isNew ? (
            <span className={`badge ${active ? "badge-success" : "badge-muted"}`}>{active ? "Active" : "Inactive"}</span>
          ) : null
        }
        subtitle={
          <button
            onClick={leave}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Request Types
          </button>
        }
        actions={
          <div className="flex items-center gap-2">
            <button className="btn btn-ghost" disabled={saving} onClick={leave}>
              Cancel
            </button>
            {!isNew &&
              (active ? (
                <button className="btn btn-outline" disabled={saving} onClick={() => setDeactivateConfirm(true)}>
                  Deactivate
                </button>
              ) : (
                <button className="btn btn-outline" disabled={saving} onClick={() => setActivation(true)}>
                  Activate
                </button>
              ))}
            <button className="btn btn-primary" disabled={saving} onClick={save}>
              {isNew ? "Create request type" : "Save"}
            </button>
          </div>
        }
      />

      <div className="container max-w-3xl space-y-6 py-6">
        {refusal && <RefusalPanel refusal={refusal} onDismiss={() => setRefusal(null)} />}

        {showIssues && issues.length > 0 && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/[0.06] p-4">
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Resolve before saving
            </div>
            <ul className="list-inside list-disc space-y-0.5 text-sm text-muted-foreground">
              {issues.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        {!isNew && !active && (
          <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
            This type is inactive: it cannot be picked for new tickets. Existing tickets, workflows and SLA
            policies still show its label.
          </div>
        )}

        {/* Identity */}
        <section className="surface rounded-xl p-6 ring-1 ring-border">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex flex-col text-sm md:col-span-2">
              <span className="mb-1 font-medium text-foreground">Label</span>
              <input
                className="input"
                placeholder="e.g. Cap Increase"
                value={draft.label}
                onChange={(e) => onLabelChange(e.target.value)}
              />
              <span className="mt-1 text-xs text-muted-foreground">
                What agents and clients see. Safe to change at any time.
              </span>
            </label>
            <div className="flex flex-col text-sm md:col-span-2">
              <span className="mb-1 font-medium text-foreground">Key</span>
              {isNew ? (
                <>
                  <input
                    className="input font-mono"
                    placeholder="e.g. cap_increase"
                    value={draft.key}
                    onChange={(e) => {
                      setKeyEdited(true);
                      set({ key: e.target.value });
                    }}
                  />
                  <span className="mt-1 text-xs text-muted-foreground">
                    Lowercase snake_case: starts with a letter, then a–z, 0–9 or _ (max 64).{" "}
                    <span className="font-medium text-foreground">Permanent</span> — it cannot be changed after
                    create.
                  </span>
                </>
              ) : (
                <>
                  <div className="flex items-center rounded-md border border-border bg-muted/30 px-3 py-2 font-mono text-sm text-foreground">
                    {draft.key}
                  </div>
                  <span className="mt-1 text-xs text-muted-foreground">
                    Read-only — joins workflows, SLA policies, Telegram commands and historical tickets. Rename by
                    editing the label.
                  </span>
                </>
              )}
            </div>
            <label className="flex flex-col text-sm md:col-span-2">
              <span className="mb-1 font-medium text-foreground">
                Description <span className="text-muted-foreground">(optional)</span>
              </span>
              <textarea
                className="textarea min-h-[72px]"
                placeholder="When to use this request type."
                value={draft.description}
                onChange={(e) => set({ description: e.target.value })}
              />
            </label>
          </div>
        </section>

        {/* Behaviour */}
        <section className="surface rounded-xl p-6 ring-1 ring-border">
          <h2 className="mb-4 text-lg font-semibold">Defaults &amp; visibility</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex flex-col text-sm">
              <span className="mb-1 font-medium text-foreground">Default priority</span>
              <select
                className="select"
                value={draft.defaultPriority}
                onChange={(e) => set({ defaultPriority: e.target.value })}
              >
                <option value="">None (Normal)</option>
                {REQUEST_TYPE_PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {TICKET_PRIORITY_LABELS[p] || p}
                  </option>
                ))}
              </select>
              <span className="mt-1 text-xs text-muted-foreground">
                Preselected when an agent creates a ticket of this type.
              </span>
            </label>
            <label className="flex flex-col text-sm">
              <span className="mb-1 font-medium text-foreground">Audience</span>
              <select className="select" value={draft.audience} onChange={(e) => set({ audience: e.target.value })}>
                <option value="internal">Internal only</option>
                <option value="internal_client">Internal + Client</option>
              </select>
              <span className="mt-1 text-xs text-muted-foreground">
                Client audience lets merchants pick it in the portal.
              </span>
            </label>
            <label className="flex items-center gap-3 text-sm md:col-span-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border"
                checked={draft.focus}
                onChange={(e) => set({ focus: e.target.checked })}
              />
              <span>
                <span className="font-medium text-foreground">Show on the Focus board</span>
                <span className="ml-2 text-muted-foreground">Tickets of this type appear in the Focus view.</span>
              </span>
            </label>
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={deactivateConfirm}
        title="Deactivate this request type?"
        description="It can no longer be picked for new tickets. Existing tickets, workflows and SLA policies keep resolving its label. You can reactivate it later."
        confirmLabel="Deactivate"
        variant="destructive"
        onCancel={() => setDeactivateConfirm(false)}
        onConfirm={() => {
          setDeactivateConfirm(false);
          setActivation(false);
        }}
      />
    </div>
  );
}

const DEPENDANT_ICONS = { telegram_command: Send, sla_policy: Timer };

function RefusalPanel({ refusal, onDismiss }) {
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/[0.06] p-4">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          {refusal.title}
        </div>
        <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
      <p className="text-sm text-muted-foreground">{refusal.message}</p>
      {refusal.dependants.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {refusal.dependants.map((d, i) => {
            const Icon = DEPENDANT_ICONS[d.kind] || AlertTriangle;
            return (
              <li
                key={`${d.kind}:${d.id ?? i}`}
                className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="text-xs uppercase tracking-wide text-muted-foreground">{d.kindLabel}</span>
                {d.kind === "sla_policy" && d.id ? (
                  <Link
                    to={`/settings/sla-policies/${d.id}`}
                    className="min-w-0 truncate text-foreground underline-offset-2 hover:underline"
                  >
                    {d.name}
                  </Link>
                ) : (
                  <span className="min-w-0 truncate text-foreground">{d.name}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default requireAuth(RequestTypeEditor);
